import React, { useEffect } from "react";
import { clsx } from "clsx";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  ShieldAlert,
  X,
} from "lucide-react";

type Tone = "danger" | "warning" | "success" | "info";

type ConfirmDialogProps = {
  open: boolean;
  title?: string;
  description?: string;
  tone?: Tone;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const toneStyles: Record<Tone, {
  icon: React.ElementType;
  badgeClass: string;
  accentClass: string;
  confirmClass: string;
}> = {
  danger: {
    icon: ShieldAlert,
    badgeClass:
      "text-red-600 bg-red-50 ring-1 ring-red-100",
    accentClass: "from-red-500 via-rose-500 to-amber-400",
    confirmClass: "bg-red-600 hover:bg-red-700 focus:ring-red-500/60",
  },
  warning: {
    icon: AlertTriangle,
    badgeClass:
      "text-amber-600 bg-amber-50 ring-1 ring-amber-100",
    accentClass: "from-amber-500 via-yellow-500 to-orange-400",
    confirmClass: "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500/60",
  },
  success: {
    icon: CheckCircle2,
    badgeClass:
      "text-emerald-600 bg-emerald-50 ring-1 ring-emerald-100",
    accentClass: "from-emerald-500 via-teal-500 to-cyan-400",
    confirmClass:
      "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500/60",
  },
  info: {
    icon: Info,
    badgeClass:
      "text-blue-600 bg-blue-50 ring-1 ring-blue-100",
    accentClass: "from-blue-600 via-sky-500 to-indigo-500",
    confirmClass: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500/60",
  },
};

/**
 * Professional confirm dialog for destructive or important actions.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title = "Xác nhận hành động",
  description = "Hãy kiểm tra kỹ trước khi tiếp tục.",
  tone = "info",
  confirmLabel = "Xác nhận",
  cancelLabel = "Hủy",
  loading = false,
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open) return null;

  const { icon: Icon, badgeClass, accentClass, confirmClass } = toneStyles[tone];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
        <div
          className={clsx(
            "absolute inset-x-0 top-0 h-1 bg-gradient-to-r",
            accentClass,
          )}
        />
        <button
          type="button"
          onClick={onCancel}
          className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500/60"
          aria-label="Đóng"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex flex-col gap-4 px-5 pb-5 pt-6">
          <div className="flex items-start gap-3">
            <span
              className={clsx(
                "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg shadow-sm",
                badgeClass,
              )}
            >
              <Icon className="h-5 w-5" />
            </span>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                {description}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
              disabled={loading}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className={clsx(
                "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2",
                confirmClass,
                loading && "opacity-80 cursor-not-allowed",
              )}
              disabled={loading}
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
